import React, { useState } from 'react';
import { DebouncedInput } from '../helpers/DebounceInput';
import { Users } from 'lucide-react';
import { Button } from 'reactstrap';
import { useSearchParams } from 'react-router-dom';
import { useGetTSPsQuery, useGetWatchlistsQuery } from '../services/edgeApi';
import Select from 'react-select';
import { useTheme } from '../helpers/hooks';
import { MultiSelectSearch } from '../helpers/MultiSelect';
import { useSelector } from 'react-redux';
import { selectTspOptions } from '../map/slice/mapSelectors';
import { Can } from '../user/Can';

export enum PATH_REPORT_FILTER_KEYS {
  WATCHLIST_IDS = 'watchlist_ids',
  PATH_NAME = 'path_name__icontains',
  BUY_POOL_TOKENS = 'buy_pool__tokens',
  SELL_POOL_TOKENS = 'sell_pool__tokens',
  TSP_LIST_TOKENS = 'tsp_list__tokens',
  RECEIPT = 'r_point_name__icontains',
  DELIVERY = 'd_point_name__icontains',
  SORT_BY = 'sort_by',
  ORDER = 'order',
  PROFIT_GT = 'profit__gt',
  PROFIT_PER_DTH_GT = 'profit_per_dth__gt',
}

// Sidebar for Path Report mode: filters on the left, results and map overlays below
export const PathReportSidebar: React.FC = () => {
  useGetTSPsQuery();
  const tspOptions = useSelector(selectTspOptions);
  const [allWatchlists, setAllWatchlists] = useState(false);
  const { data: watchlists = [], isLoading } = useGetWatchlistsQuery({
    view_all: allWatchlists,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [appTheme] = useTheme();

  const parseIds = (params: URLSearchParams): number[] => {
    const values = params.getAll(PATH_REPORT_FILTER_KEYS.WATCHLIST_IDS);
    return values.map((v) => parseInt(v, 10)).filter((v) => !isNaN(v));
  };

  const selectedWatchlistIds = parseIds(searchParams);
  const getParam = (key: string, def: string = '') =>
    searchParams.get(key) || def;
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value && value.trim().length > 0) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };
  const options = (watchlists ?? []).map((wl: any) => ({
    value: wl.id,
    label: allWatchlists ? `${wl.name} - ${wl.user_email}` : wl.name,
  }));

  const selectedOptions = options.filter((o) =>
    selectedWatchlistIds.includes(o.value),
  );

  const onWatchlistsChange = (selected: any) => {
    const ids = Array.isArray(selected) ? selected.map((o) => o.value) : [];
    const params = new URLSearchParams(searchParams);
    params.delete(PATH_REPORT_FILTER_KEYS.WATCHLIST_IDS);
    ids.forEach((id) =>
      params.append(PATH_REPORT_FILTER_KEYS.WATCHLIST_IDS, id),
    );
    setSearchParams(params);
  };

  return (
    <>
      <div className="mb-3 d-flex flex-column gap-2">
        <label className="form-label small">Watchlists</label>
        <div className="d-flex gap-2 align-items-center">
          <Select
            isMulti
            isLoading={isLoading}
            options={options}
            value={selectedOptions}
            onChange={onWatchlistsChange}
            classNamePrefix="react-select"
            placeholder="Select watchlists…"
            closeMenuOnSelect={false}
            styles={{ container: (base) => ({ ...base, width: '100%' }) }}
            theme={(theme) => ({
              ...theme,
              borderRadius: 4,
              colors:
                appTheme === 'light'
                  ? theme.colors
                  : {
                      ...theme.colors,
                      primary: 'var(--bs-body-color)',
                      primary75: 'var(--bs-body-color)',
                      primary25: 'var(--bs-secondary-bg)',
                      neutral0: 'var(--bs-body-bg)',
                      neutral10: 'var(--bs-secondary-bg)',
                      neutral20: 'var(--bs-tertiary-bg)',
                      neutral30: 'var(--bs-secondary-color)',
                      neutral50: 'var(--bs-body-color)',
                      neutral60: 'var(--bs-body-color)',
                      neutral80: 'var(--bs-body-color)',
                      neutral90: 'var(--bs-body-color)',
                    },
            })}
          />

          <Can can={['add_point']}>
            <Button
              type="button"
              size="xs"
              color={allWatchlists ? 'warning' : 'secondary'}
              outline={!allWatchlists}
              active={allWatchlists}
              title={
                allWatchlists
                  ? 'All Watchlists (including other users) are shown'
                  : 'Only your Watchlists are shown'
              }
              aria-pressed={allWatchlists}
              onClick={() => setAllWatchlists((prev) => !prev)}
            >
              <Users />
            </Button>
          </Can>
        </div>
        <div className="d-flex flex-column">
          <label className="form-label small">Name</label>
          <DebouncedInput
            className="form-control w-100"
            placeholder="Filter Name"
            value={getParam(PATH_REPORT_FILTER_KEYS.PATH_NAME)}
            onChange={(value) =>
              setParam(PATH_REPORT_FILTER_KEYS.PATH_NAME, String(value))
            }
          />
        </div>

        <div className="d-flex flex-column">
          <label className="form-label small">TSPs</label>
          <MultiSelectSearch
            options={tspOptions}
            onChange={(tsps) =>
              setParam(
                PATH_REPORT_FILTER_KEYS.TSP_LIST_TOKENS,
                tsps.map(({ label }) => label).join(', '),
              )
            }
          />
        </div>
        <div className="d-flex flex-column">
          <label className="form-label small">Buy Pool</label>
          <DebouncedInput
            className="form-control w-100"
            placeholder="Filter Buy Pool (comma, -negation; any)"
            value={getParam(PATH_REPORT_FILTER_KEYS.BUY_POOL_TOKENS)}
            onChange={(value) =>
              setParam(PATH_REPORT_FILTER_KEYS.BUY_POOL_TOKENS, String(value))
            }
          />
        </div>
        <div className="d-flex flex-column">
          <label className="form-label small">Sell Pool</label>
          <DebouncedInput
            className="form-control w-100"
            placeholder="Filter Sell Pool (comma, -negation; any)"
            value={getParam(PATH_REPORT_FILTER_KEYS.SELL_POOL_TOKENS)}
            onChange={(value) =>
              setParam(PATH_REPORT_FILTER_KEYS.SELL_POOL_TOKENS, String(value))
            }
          />
        </div>
        <div className="d-flex flex-column">
          <label className="form-label small">Receipt</label>
          <DebouncedInput
            className="form-control w-100"
            placeholder="Filter Receipt"
            value={getParam(PATH_REPORT_FILTER_KEYS.RECEIPT)}
            onChange={(value) =>
              setParam(PATH_REPORT_FILTER_KEYS.RECEIPT, String(value))
            }
          />
        </div>
        <div className="d-flex flex-column">
          <label className="form-label small">Delivery</label>
          <DebouncedInput
            className="form-control w-100"
            placeholder="Filter Delivery"
            value={getParam(PATH_REPORT_FILTER_KEYS.DELIVERY)}
            onChange={(value) =>
              setParam(PATH_REPORT_FILTER_KEYS.DELIVERY, String(value))
            }
          />
        </div>

        <div className="row">
          <div className="col-6 col-xs-12 d-flex flex-column">
            <label className="form-label small">Sort By</label>
            <select
              className="form-select w-100"
              value={getParam(PATH_REPORT_FILTER_KEYS.SORT_BY, 'profit')}
              onChange={(e) =>
                setParam(PATH_REPORT_FILTER_KEYS.SORT_BY, e.target.value)
              }
            >
              <option value="profit_per_dth">$/dth</option>
              <option value="profit">Profit</option>
              <option value="max_volume">Max Volume</option>
            </select>
          </div>

          <div className="col-6 col-xs-12 d-flex flex-column">
            <label className="form-label small">Order</label>
            <select
              className="form-select w-100"
              value={getParam(PATH_REPORT_FILTER_KEYS.ORDER, 'desc')}
              onChange={(e) =>
                setParam(PATH_REPORT_FILTER_KEYS.ORDER, e.target.value)
              }
            >
              <option value="desc">Desc</option>
              <option value="asc">Asc</option>
            </select>
          </div>
        </div>

        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="positiveProfitOnly"
            checked={Boolean(getParam(PATH_REPORT_FILTER_KEYS.PROFIT_GT))}
            onChange={(e) =>
              setParam(
                PATH_REPORT_FILTER_KEYS.PROFIT_GT,
                e.target.checked ? '0' : '',
              )
            }
          />
          <label className="form-check-label" htmlFor="positiveProfitOnly">
            Profit &gt; 0
          </label>
        </div>
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="positivePpdOnly"
            checked={Boolean(
              getParam(PATH_REPORT_FILTER_KEYS.PROFIT_PER_DTH_GT),
            )}
            onChange={(e) =>
              setParam(
                PATH_REPORT_FILTER_KEYS.PROFIT_PER_DTH_GT,
                e.target.checked ? '0' : '',
              )
            }
          />
          <label className="form-check-label" htmlFor="positivePpdOnly">
            $ / dth &gt; 0
          </label>
        </div>
      </div>

      {/* Table is rendered in the PathReport tab; sidebar controls update URL params */}
    </>
  );
};
